import {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useRef,
} from "react";
import { useSelector } from "react-redux";
import { INITIAL_ADVERTISEMENTS, INITIAL_DEVICE_LOG } from "../data/mockData";

/* ── Initial state ─────────────────────────────────────── */
// A factory so each reset gets fresh Set instances (never shared references).
const makeInitialState = () => ({
  advertisements: INITIAL_ADVERTISEMENTS,
  deviceLog: INITIAL_DEVICE_LOG,
  currentAdName: "",
  currentAdId: null,
  selectedFleets: new Set(),
  selectedFleetName: "",
  selectedDevices: new Set(),
});

const initialState = makeInitialState();

/* ── Reducer ───────────────────────────────────────────── */
function appReducer(state, action) {
  switch (action.type) {
    case "OPEN_REPORT":
      // payload may be a plain name (legacy) or { id, name } for the detail page.
      return {
        ...state,
        currentAdName: action.payload?.name ?? action.payload,
        currentAdId: action.payload?.id ?? null,
      };

    case "CREATE_ADVERTISEMENT":
      return {
        ...state,
        advertisements: [action.payload, ...state.advertisements],
      };

    case "ROLLBACK_ADVERTISEMENT":
      return {
        ...state,
        advertisements: state.advertisements.map((a) =>
          a.id === action.payload ? { ...a, status: "Rolled back" } : a,
        ),
      };

    case "ROLLBACK_IMEI":
      return {
        ...state,
        deviceLog: state.deviceLog.map((r) =>
          r.id === action.payload ? { ...r, rolledBack: true } : r,
        ),
      };

    case "ROLLBACK_IMEIS":
      return {
        ...state,
        deviceLog: state.deviceLog.map((r) =>
          action.payload.includes(r.id) ? { ...r, rolledBack: true } : r,
        ),
      };

    case "SET_SELECTED_FLEETS": {
      // payload may be an array of ids (legacy) or { ids, name } so the
      // schedule page can show the fleet name without mock data.
      const nextIds = action.payload.ids ?? action.payload;
      const prevIds = [...state.selectedFleets];
      // If the chosen fleet set actually changed, any previously-selected
      // devices belong to the old fleet and must be dropped — otherwise stale
      // IMEIs carry over and show as selected on the new fleet's device list.
      const sameFleet =
        prevIds.length === nextIds.length &&
        prevIds.every((id) => nextIds.includes(id));
      return {
        ...state,
        selectedFleets: new Set(nextIds),
        selectedFleetName: action.payload.name ?? state.selectedFleetName,
        selectedDevices: sameFleet ? state.selectedDevices : new Set(),
      };
    }

    case "SET_SELECTED_DEVICES":
      return { ...state, selectedDevices: new Set(action.payload) };

    // Clear fleet + device selection when starting a fresh scheduling flow so a
    // previous advertisement's choices never leak into a new one.
    case "RESET_ADV_SELECTION":
      return {
        ...state,
        selectedFleets: new Set(),
        selectedFleetName: "",
        selectedDevices: new Set(),
      };

    // Wipe the entire context back to its initial state. Used on logout so no
    // data (selections, current report, etc.) is preserved between users.
    case "RESET_APP":
      return makeInitialState();

    default:
      return state;
  }
}

/* ── Context ───────────────────────────────────────────── */
const AppContext = createContext(null);

export function AppProvider({ children }) {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Wipe the whole context on logout so no data (selections, current report,
  // etc.) carries over between users. Watches the Redux auth login flag and
  // resets when it flips from logged-in to logged-out.
  const isLoggedIn = useSelector((s) => s.auth?.islogin);
  const wasLoggedIn = useRef(isLoggedIn);
  useEffect(() => {
    if (wasLoggedIn.current && !isLoggedIn) {
      dispatch({ type: "RESET_APP" });
    }
    wasLoggedIn.current = isLoggedIn;
  }, [isLoggedIn]);

  return (
    <AppContext.Provider value={{ state, dispatch }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used inside <AppProvider>");
  return ctx;
}
